import { useState, useEffect } from "react";
import axios from "axios";
import { API, useAuth } from "@/App";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  Car, Plus, Phone, Mail, MessageSquare, MapPin, Edit2, Trash2, Wrench,
  Shield, FileCheck, CircleDashed, AlertTriangle, ArrowLeft, Receipt, NotebookPen,
} from "lucide-react";
import PageHelp from "@/components/PageHelp";
import { CarnetRoute } from "@/components/CarnetRoute";
import { PlanEntretien } from "@/components/PlanEntretien";
import { ProblemesVehicule } from "@/components/ProblemesVehicule";
import { DashboardVehicule } from "@/components/DashboardVehicule";

const ALERT_COLORS = {
  expired: "bg-red-100 text-red-700 border-red-200",
  critical: "bg-red-50 text-red-700 border-red-200",
  warning: "bg-amber-50 text-amber-700 border-amber-200",
  ok: "bg-emerald-50 text-emerald-700 border-emerald-200",
};

const CONTACT_TYPES = [
  { value: "garage", label: "Garage", icon: Wrench },
  { value: "controle_technique", label: "Contrôle technique", icon: FileCheck },
  { value: "assurance", label: "Assurance", icon: Shield },
  { value: "pneus", label: "Pneus", icon: CircleDashed },
  { value: "depannage", label: "Dépannage", icon: AlertTriangle },
  { value: "autre", label: "Autre", icon: NotebookPen },
];

const ENTRETIEN_TYPES = [
  { value: "vidange", label: "Vidange" },
  { value: "controle_technique", label: "Contrôle technique" },
  { value: "assurance", label: "Assurance" },
  { value: "taxe", label: "Taxe circulation" },
  { value: "pneus", label: "Pneus" },
  { value: "reparation", label: "Réparation" },
  { value: "autre", label: "Autre" },
];

const emptyVehicule = () => ({
  plaque: "", marque: "", modele: "", annee: "",
  kilometrage: 0, carburant: "diesel", consommation: "", notes: "",
  prochain_controle_technique: "", prochaine_vidange_date: "",
  prochaine_vidange_km: "", prochaine_assurance: "",
  prochaine_taxe: "", prochain_changement_pneus: "",
});

const Vehicules = () => {
  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };

  const [vehicules, setVehicules] = useState([]);
  const [stats, setStats] = useState({ cost_month: 0, cost_year: 0 });
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);  // null = list view, object = detail
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyVehicule());
  // sub-dialogs
  const [entretienOpen, setEntretienOpen] = useState(false);
  const [entretienForm, setEntretienForm] = useState({ type: "vidange", date: "", km: "", cout: 0, garage: "", notes: "" });
  const [contactOpen, setContactOpen] = useState(false);
  const [contactForm, setContactForm] = useState({ type: "garage", nom_entreprise: "", nom_contact: "", telephone: "", email: "", adresse: "", horaires: "", notes: "" });
  const [editingContact, setEditingContact] = useState(null);

  const fetchAll = async () => {
    try {
      const [v, s] = await Promise.all([
        axios.get(`${API}/vehicules`, { headers }),
        axios.get(`${API}/vehicules/stats/overview`, { headers }),
      ]);
      setVehicules(v.data);
      setStats(s.data);
      // If a vehicle was selected, refresh it with the latest data
      if (selected) {
        const fresh = v.data.find(x => x.id === selected.id);
        if (fresh) setSelected(fresh);
      }
    } catch { toast.error("Erreur chargement"); }
    finally { setLoading(false); }
  };
  useEffect(() => { fetchAll(); }, []); // eslint-disable-line

  const openCreate = () => { setEditing(null); setForm(emptyVehicule()); setDialogOpen(true); };
  const openEdit = (v) => {
    setEditing(v);
    setForm({
      plaque: v.plaque, marque: v.marque || "", modele: v.modele || "",
      annee: v.annee || "", kilometrage: v.kilometrage || 0,
      carburant: v.carburant || "diesel", consommation: v.consommation || "",
      notes: v.notes || "",
      prochain_controle_technique: v.prochain_controle_technique || "",
      prochaine_vidange_date: v.prochaine_vidange_date || "",
      prochaine_vidange_km: v.prochaine_vidange_km || "",
      prochaine_assurance: v.prochaine_assurance || "",
      prochaine_taxe: v.prochaine_taxe || "",
      prochain_changement_pneus: v.prochain_changement_pneus || "",
    });
    setDialogOpen(true);
  };

  const handleSaveVehicule = async (e) => {
    e.preventDefault();
    if (!form.plaque) { toast.error("Plaque requise"); return; }
    const payload = { ...form };
    // Clean numeric / empty fields
    payload.kilometrage = Number(payload.kilometrage) || 0;
    payload.annee = payload.annee ? Number(payload.annee) : null;
    payload.consommation = payload.consommation ? Number(payload.consommation) : null;
    payload.prochaine_vidange_km = payload.prochaine_vidange_km ? Number(payload.prochaine_vidange_km) : null;
    ["prochain_controle_technique","prochaine_vidange_date","prochaine_assurance","prochaine_taxe","prochain_changement_pneus"].forEach(k => {
      if (!payload[k]) payload[k] = null;
    });
    try {
      if (editing) {
        await axios.put(`${API}/vehicules/${editing.id}`, payload, { headers });
        toast.success("Véhicule mis à jour");
      } else {
        await axios.post(`${API}/vehicules`, payload, { headers });
        toast.success("Véhicule ajouté");
      }
      setDialogOpen(false);
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
  };

  const handleDeleteVehicule = async (v) => {
    if (!window.confirm(`Supprimer le véhicule ${v.plaque} ?`)) return;
    try { await axios.delete(`${API}/vehicules/${v.id}`, { headers }); toast.success("Supprimé"); setSelected(null); fetchAll(); }
    catch { toast.error("Erreur"); }
  };

  // --- Entretien ---
  const openEntretien = () => {
    setEntretienForm({ type: "vidange", date: new Date().toISOString().slice(0, 10), km: "", cout: 0, garage: "", notes: "" });
    setEntretienOpen(true);
  };
  const saveEntretien = async (e) => {
    e.preventDefault();
    if (!selected) return;
    const payload = {
      type: entretienForm.type,
      date: entretienForm.date,
      km: entretienForm.km ? Number(entretienForm.km) : null,
      cout: Number(entretienForm.cout) || 0,
      garage: entretienForm.garage,
      notes: entretienForm.notes,
    };
    try { await axios.post(`${API}/vehicules/${selected.id}/entretiens`, payload, { headers }); toast.success("Entretien ajouté"); setEntretienOpen(false); fetchAll(); }
    catch { toast.error("Erreur"); }
  };
  const deleteEntretien = async (eid) => {
    if (!selected || !window.confirm("Supprimer cet entretien ?")) return;
    try { await axios.delete(`${API}/vehicules/${selected.id}/entretiens/${eid}`, { headers }); fetchAll(); } catch { toast.error("Erreur"); }
  };

  // --- Contacts ---
  const openContact = (c = null) => {
    if (c) { setEditingContact(c); setContactForm({ ...c }); }
    else { setEditingContact(null); setContactForm({ type: "garage", nom_entreprise: "", nom_contact: "", telephone: "", email: "", adresse: "", horaires: "", notes: "" }); }
    setContactOpen(true);
  };
  const saveContact = async (e) => {
    e.preventDefault();
    if (!selected) return;
    if (!contactForm.nom_entreprise) { toast.error("Nom entreprise requis"); return; }
    try {
      if (editingContact) {
        await axios.put(`${API}/vehicules/${selected.id}/contacts/${editingContact.id}`, contactForm, { headers });
        toast.success("Contact mis à jour");
      } else {
        await axios.post(`${API}/vehicules/${selected.id}/contacts`, contactForm, { headers });
        toast.success("Contact ajouté");
      }
      setContactOpen(false);
      fetchAll();
    } catch { toast.error("Erreur"); }
  };
  const deleteContact = async (cid) => {
    if (!selected || !window.confirm("Supprimer ce contact ?")) return;
    try { await axios.delete(`${API}/vehicules/${selected.id}/contacts/${cid}`, { headers }); fetchAll(); } catch { toast.error("Erreur"); }
  };

  // ========== Render ==========
  if (loading) {
    return <div className="flex justify-center py-20"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-indigo-600"></div></div>;
  }

  // ---- Detail view ----
  if (selected) {
    const v = selected;
    const alerts = (v.alerts || []).filter(a => a.level !== "ok");
    const totalEntretiens = (v.entretiens || []).reduce((a, e) => a + (e.cout || 0), 0);

    return (
      <div className="space-y-5 animate-fade-in" data-testid="vehicule-detail">
        <Button variant="ghost" onClick={() => setSelected(null)} data-testid="back-to-list"><ArrowLeft className="w-4 h-4 mr-1" />Retour à la liste</Button>

        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-3xl font-bold text-slate-800 flex items-center gap-2">
              <Car className="w-7 h-7 text-indigo-600" />
              {v.marque} {v.modele}
            </h1>
            <p className="text-sm text-slate-500 font-mono">{v.plaque}</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => openEdit(v)} data-testid="edit-vehicule"><Edit2 className="w-4 h-4 mr-1" />Modifier</Button>
            <Button variant="outline" className="text-red-500" onClick={() => handleDeleteVehicule(v)}><Trash2 className="w-4 h-4 mr-1" />Supprimer</Button>
          </div>
        </div>

        {alerts.length > 0 && (
          <Card className="border-amber-200 bg-amber-50/30">
            <CardContent className="p-4">
              <div className="flex items-start gap-2">
                <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div className="flex-1 space-y-1">
                  <p className="font-semibold text-amber-900 text-sm">Échéances à surveiller</p>
                  {alerts.map((a, i) => (
                    <p key={i} className="text-sm text-amber-800">
                      • <b>{a.label}</b> — {a.days_left !== undefined
                        ? (a.days_left < 0 ? `En retard de ${Math.abs(a.days_left)} jours 🔴` : `${a.days_left} jours restants`)
                        : a.km_left !== undefined
                          ? (a.km_left <= 0 ? `dépassé de ${Math.abs(a.km_left)} km 🔴` : `${a.km_left} km restants`)
                          : (a.detail || "à traiter")}
                      {a.date && <span className="text-xs text-amber-600 ml-2">({a.date})</span>}
                    </p>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Informations */}
        <Card>
          <CardHeader><CardTitle className="text-lg">📋 Informations</CardTitle></CardHeader>
          <CardContent className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
            <div><p className="text-slate-400">Plaque</p><p className="font-medium">{v.plaque}</p></div>
            <div><p className="text-slate-400">Marque / Modèle</p><p className="font-medium">{v.marque} {v.modele || "—"}</p></div>
            <div><p className="text-slate-400">Année</p><p className="font-medium">{v.annee || "—"}</p></div>
            <div><p className="text-slate-400">Kilométrage</p><p className="font-medium">{v.kilometrage?.toLocaleString() || 0} km</p></div>
            <div><p className="text-slate-400">Carburant</p><p className="font-medium capitalize">{v.carburant}</p></div>
            <div><p className="text-slate-400">Consommation</p><p className="font-medium">{v.consommation ? `${v.consommation} L/100km` : "—"}</p></div>
          </CardContent>
        </Card>

        {/* Échéances (lecture seule — éditées via Modifier) */}
        <Card>
          <CardHeader><CardTitle className="text-lg">🔧 Prochaines échéances</CardTitle></CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
            {[
              ["Contrôle technique", v.prochain_controle_technique],
              ["Vidange (date)", v.prochaine_vidange_date],
              ["Vidange (km)", v.prochaine_vidange_km ? `${v.prochaine_vidange_km.toLocaleString()} km` : null],
              ["Assurance", v.prochaine_assurance],
              ["Taxe circulation", v.prochaine_taxe],
              ["Changement pneus", v.prochain_changement_pneus],
            ].map(([label, value]) => (
              <div key={label} className="flex justify-between py-2 border-b border-slate-100">
                <span className="text-slate-500">{label}</span>
                <span className="font-medium">{value || "—"}</span>
              </div>
            ))}
          </CardContent>
        </Card>

        {/* Notes */}
        {v.notes && (
          <Card>
            <CardHeader><CardTitle className="text-lg">📝 Notes</CardTitle></CardHeader>
            <CardContent className="text-sm text-slate-700 whitespace-pre-wrap">{v.notes}</CardContent>
          </Card>
        )}

        {/* Dashboard véhicule */}
        <DashboardVehicule vehicule={v} />

        {/* Problèmes signalés */}
        <ProblemesVehicule vehicule={v} onChange={fetchAll} />

        {/* Carnet de route */}
        <CarnetRoute vehicule={v} onChange={fetchAll} />

        {/* Planning entretien */}
        <PlanEntretien vehicule={v} onChange={fetchAll} />

        {/* Contacts */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-lg flex items-center gap-2">📞 Contacts ({(v.contacts || []).length})</CardTitle>
            <Button size="sm" onClick={() => openContact()} className="bg-indigo-600 hover:bg-indigo-700" data-testid="add-contact-btn"><Plus className="w-4 h-4 mr-1" />Ajouter</Button>
          </CardHeader>
          <CardContent>
            {(v.contacts || []).length === 0 ? (
              <p className="text-sm text-slate-400 text-center py-6">Aucun contact pour ce véhicule</p>
            ) : (
              <div className="space-y-3">
                {v.contacts.map(c => {
                  const typeInfo = CONTACT_TYPES.find(t => t.value === c.type) || CONTACT_TYPES[0];
                  const Icon = typeInfo.icon;
                  const smsText = encodeURIComponent(`Bonjour ${c.nom_contact || ""}, je vous contacte au sujet de mon véhicule ${v.plaque} (${v.marque} ${v.modele}). `);
                  return (
                    <div key={c.id} className="border rounded-lg p-3 bg-slate-50/50" data-testid={`contact-${c.id}`}>
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex items-start gap-3 flex-1">
                          <div className="w-9 h-9 rounded-full bg-indigo-100 text-indigo-600 flex items-center justify-center"><Icon className="w-4 h-4" /></div>
                          <div>
                            <p className="text-xs text-slate-400 uppercase tracking-wider">{typeInfo.label}</p>
                            <p className="font-semibold text-slate-800">{c.nom_entreprise}</p>
                            {c.nom_contact && <p className="text-sm text-slate-600">{c.nom_contact}</p>}
                            {c.adresse && <p className="text-xs text-slate-500 mt-0.5">📍 {c.adresse}</p>}
                            {c.horaires && <p className="text-xs text-slate-500">🕒 {c.horaires}</p>}
                            {c.notes && <p className="text-xs text-slate-500 italic mt-1">{c.notes}</p>}
                          </div>
                        </div>
                        <div className="flex gap-1">
                          <Button size="sm" variant="ghost" onClick={() => openContact(c)}><Edit2 className="w-3.5 h-3.5" /></Button>
                          <Button size="sm" variant="ghost" className="text-red-500" onClick={() => deleteContact(c.id)}><Trash2 className="w-3.5 h-3.5" /></Button>
                        </div>
                      </div>
                      <div className="flex flex-wrap gap-2 mt-3">
                        {c.telephone && <a href={`tel:${c.telephone}`} className="inline-flex items-center gap-1 text-xs bg-emerald-600 hover:bg-emerald-700 text-white px-3 py-1.5 rounded-md" data-testid={`contact-call-${c.id}`}><Phone className="w-3 h-3" />Appeler</a>}
                        {c.telephone && <a href={`sms:${c.telephone}?body=${smsText}`} className="inline-flex items-center gap-1 text-xs bg-indigo-600 hover:bg-indigo-700 text-white px-3 py-1.5 rounded-md"><MessageSquare className="w-3 h-3" />SMS</a>}
                        {c.email && <a href={`mailto:${c.email}?subject=${encodeURIComponent("Véhicule "+v.plaque)}`} className="inline-flex items-center gap-1 text-xs bg-slate-600 hover:bg-slate-700 text-white px-3 py-1.5 rounded-md"><Mail className="w-3 h-3" />Email</a>}
                        {c.adresse && <a href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(c.adresse)}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs bg-amber-600 hover:bg-amber-700 text-white px-3 py-1.5 rounded-md"><MapPin className="w-3 h-3" />Localiser</a>}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Historique entretiens */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle className="text-lg flex items-center gap-2">📜 Historique des entretiens ({(v.entretiens || []).length})</CardTitle>
              <CardDescription>Coût total : <b>{totalEntretiens.toFixed(2)} €</b></CardDescription>
            </div>
            <Button size="sm" onClick={openEntretien} className="bg-indigo-600 hover:bg-indigo-700" data-testid="add-entretien-btn"><Plus className="w-4 h-4 mr-1" />Ajouter</Button>
          </CardHeader>
          <CardContent>
            {(v.entretiens || []).length === 0 ? (
              <p className="text-sm text-slate-400 text-center py-6">Aucun entretien enregistré</p>
            ) : (
              <div className="space-y-2">
                {[...v.entretiens].sort((a, b) => (b.date || "").localeCompare(a.date || "")).map(e => {
                  const typeLabel = ENTRETIEN_TYPES.find(t => t.value === e.type)?.label || e.type;
                  return (
                    <div key={e.id} className="flex items-center justify-between py-2 border-b border-slate-100 text-sm" data-testid={`entretien-${e.id}`}>
                      <div className="flex items-center gap-3">
                        <Wrench className="w-4 h-4 text-slate-400" />
                        <div>
                          <p className="font-medium">{typeLabel}</p>
                          <p className="text-xs text-slate-500">{e.date}{e.km ? ` · ${e.km.toLocaleString()} km` : ""}{e.garage ? ` · ${e.garage}` : ""}</p>
                          {e.notes && <p className="text-xs text-slate-400 italic">{e.notes}</p>}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="font-bold text-indigo-600">{(e.cout || 0).toFixed(2)} €</span>
                        <Button size="sm" variant="ghost" className="text-red-500 h-7 w-7 p-0" onClick={() => deleteEntretien(e.id)}><Trash2 className="w-3.5 h-3.5" /></Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Dialog entretien */}
        <Dialog open={entretienOpen} onOpenChange={setEntretienOpen}>
          <DialogContent data-testid="entretien-dialog">
            <DialogHeader><DialogTitle>Nouvel entretien</DialogTitle></DialogHeader>
            <form onSubmit={saveEntretien} className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Type</Label><Select value={entretienForm.type} onValueChange={v => setEntretienForm({ ...entretienForm, type: v })}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{ENTRETIEN_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent></Select></div>
                <div><Label>Date</Label><Input type="date" value={entretienForm.date} onChange={e => setEntretienForm({ ...entretienForm, date: e.target.value })} required /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Km au compteur</Label><Input type="number" value={entretienForm.km} onChange={e => setEntretienForm({ ...entretienForm, km: e.target.value })} /></div>
                <div><Label>Coût (€)</Label><Input type="number" step="0.01" value={entretienForm.cout} onChange={e => setEntretienForm({ ...entretienForm, cout: e.target.value })} /></div>
              </div>
              <div><Label>Garage / Prestataire</Label><Input value={entretienForm.garage} onChange={e => setEntretienForm({ ...entretienForm, garage: e.target.value })} placeholder="Ex: Garage Dupont" /></div>
              <div><Label>Notes</Label><Textarea rows={2} value={entretienForm.notes} onChange={e => setEntretienForm({ ...entretienForm, notes: e.target.value })} /></div>
              <Button type="submit" className="w-full bg-indigo-600 hover:bg-indigo-700">Enregistrer</Button>
            </form>
          </DialogContent>
        </Dialog>

        {/* Dialog contact */}
        <Dialog open={contactOpen} onOpenChange={setContactOpen}>
          <DialogContent data-testid="contact-dialog" className="max-w-lg">
            <DialogHeader><DialogTitle>{editingContact ? "Modifier le contact" : "Nouveau contact"}</DialogTitle></DialogHeader>
            <form onSubmit={saveContact} className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Type *</Label><Select value={contactForm.type} onValueChange={v => setContactForm({ ...contactForm, type: v })}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{CONTACT_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent></Select></div>
                <div><Label>Nom entreprise *</Label><Input value={contactForm.nom_entreprise} onChange={e => setContactForm({ ...contactForm, nom_entreprise: e.target.value })} required /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Nom contact</Label><Input value={contactForm.nom_contact} onChange={e => setContactForm({ ...contactForm, nom_contact: e.target.value })} placeholder="Personne à contacter" /></div>
                <div><Label>Téléphone</Label><Input value={contactForm.telephone} onChange={e => setContactForm({ ...contactForm, telephone: e.target.value })} placeholder="+32 ..." /></div>
              </div>
              <div><Label>Email</Label><Input type="email" value={contactForm.email} onChange={e => setContactForm({ ...contactForm, email: e.target.value })} /></div>
              <div><Label>Adresse</Label><Input value={contactForm.adresse} onChange={e => setContactForm({ ...contactForm, adresse: e.target.value })} /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Horaires</Label><Input value={contactForm.horaires} onChange={e => setContactForm({ ...contactForm, horaires: e.target.value })} placeholder="Lun-Ven 8h-18h" /></div>
                <div><Label>Notes</Label><Input value={contactForm.notes} onChange={e => setContactForm({ ...contactForm, notes: e.target.value })} /></div>
              </div>
              <Button type="submit" className="w-full bg-indigo-600 hover:bg-indigo-700">{editingContact ? "Enregistrer" : "Ajouter"}</Button>
            </form>
          </DialogContent>
        </Dialog>

        {/* Dialog véhicule (modifier) */}
        <VehiculeFormDialog open={dialogOpen} onOpenChange={setDialogOpen} form={form} setForm={setForm} onSave={handleSaveVehicule} editing={editing} />
      </div>
    );
  }

  // ---- List view ----
  return (
    <div className="space-y-6 animate-fade-in" data-testid="vehicules-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-3xl font-bold text-slate-800 flex items-center gap-2"><Car className="w-7 h-7 text-indigo-600" />Véhicules</h1>
            <PageHelp title="Véhicules" emoji="🚐">
              <p>Gestion de ta flotte : camionnettes, entretiens, contacts garagistes et échéances.</p>
              <ul className="space-y-1.5 list-disc pl-5">
                <li><b>Clic sur une carte</b> → voir les détails, ajouter entretien/contact</li>
                <li><b>Alertes auto</b> — L'app te prévient 30 jours avant chaque échéance (CT, assurance, vidange…)</li>
                <li><b>Contacts pros</b> — chaque véhicule a son carnet (garage, assurance, pneus, dépannage…) avec boutons Appeler/SMS/Email/Localiser</li>
                <li><b>Historique</b> — toutes tes interventions, calcul auto du coût total</li>
              </ul>
            </PageHelp>
          </div>
          <p className="text-slate-500 mt-1">Entretiens, échéances et carnet de contacts par camionnette</p>
        </div>
        <Button onClick={openCreate} className="bg-indigo-600 hover:bg-indigo-700" data-testid="add-vehicule-btn"><Plus className="w-4 h-4 mr-2" />Ajouter un véhicule</Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card><CardContent className="p-4"><p className="text-sm text-slate-500">Véhicules</p><p className="text-2xl font-bold text-slate-800">{stats.nb_vehicules || vehicules.length}</p></CardContent></Card>
        <Card><CardContent className="p-4"><p className="text-sm text-slate-500 flex items-center gap-1"><Receipt className="w-3.5 h-3.5" />Coût ce mois ({stats.month})</p><p className="text-2xl font-bold text-indigo-600">{stats.cost_month?.toFixed(2)} €</p></CardContent></Card>
        <Card><CardContent className="p-4"><p className="text-sm text-slate-500">Coût {stats.year}</p><p className="text-2xl font-bold text-emerald-600">{stats.cost_year?.toFixed(2)} €</p></CardContent></Card>
      </div>

      {vehicules.length === 0 ? (
        <Card><CardContent className="py-16 text-center text-slate-400"><Car className="w-12 h-12 mx-auto mb-3 opacity-40" /><p>Aucun véhicule enregistré</p><p className="text-sm mt-1">Clique "Ajouter un véhicule" pour commencer</p></CardContent></Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {vehicules.map(v => {
            const alerts = (v.alerts || []).filter(a => a.level !== "ok");
            const worst = alerts.reduce((w, a) => {
              const order = { expired: 3, critical: 2, warning: 1 };
              return order[a.level] > (order[w?.level] || 0) ? a : w;
            }, null);
            return (
              <Card key={v.id} className="hover:shadow-md transition-shadow cursor-pointer" onClick={() => setSelected(v)} data-testid={`vehicule-card-${v.id}`}>
                <CardContent className="p-4 space-y-3">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="font-bold text-slate-800">{v.marque} {v.modele}</p>
                      <p className="text-xs font-mono text-slate-500">{v.plaque}</p>
                    </div>
                    <Car className="w-6 h-6 text-indigo-400" />
                  </div>
                  <div className="text-sm space-y-1 text-slate-600">
                    <p>🛣️ {v.kilometrage?.toLocaleString() || 0} km</p>
                    <p>⛽ {v.carburant} {v.consommation ? `· ${v.consommation} L/100km` : ""}</p>
                  </div>
                  {worst ? (
                    <div className={`text-xs px-2 py-1.5 rounded-lg border ${ALERT_COLORS[worst.level]}`}>
                      {worst.level === "expired" ? "🔴 " : worst.level === "critical" ? "🟠 " : "⚠️ "}
                      <b>{worst.label}</b> — {worst.days_left !== undefined ? (worst.days_left < 0 ? `retard ${Math.abs(worst.days_left)}j` : `${worst.days_left}j restants`) : worst.km_left !== undefined ? `${worst.km_left} km` : (worst.detail || "à traiter")}
                    </div>
                  ) : (
                    <div className="text-xs px-2 py-1.5 rounded-lg border bg-emerald-50 text-emerald-700 border-emerald-200">✅ Toutes les échéances sont à jour</div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Dialog créer/modifier véhicule */}
      <VehiculeFormDialog open={dialogOpen} onOpenChange={setDialogOpen} form={form} setForm={setForm} onSave={handleSaveVehicule} editing={editing} />
    </div>
  );
};

// ---- Separate dialog to avoid duplication ----
const VehiculeFormDialog = ({ open, onOpenChange, form, setForm, onSave, editing }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="vehicule-dialog">
      <DialogHeader><DialogTitle>{editing ? `Modifier ${editing.plaque}` : "Ajouter un véhicule"}</DialogTitle></DialogHeader>
      <form onSubmit={onSave} className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <div><Label>Plaque *</Label><Input value={form.plaque} onChange={e => setForm({ ...form, plaque: e.target.value })} placeholder="1-ABC-123" required data-testid="vehicule-plaque" /></div>
          <div><Label>Année</Label><Input type="number" value={form.annee} onChange={e => setForm({ ...form, annee: e.target.value })} placeholder="2019" /></div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div><Label>Marque</Label><Input value={form.marque} onChange={e => setForm({ ...form, marque: e.target.value })} placeholder="Renault" /></div>
          <div><Label>Modèle</Label><Input value={form.modele} onChange={e => setForm({ ...form, modele: e.target.value })} placeholder="Master" /></div>
        </div>
        <div className="grid grid-cols-3 gap-3">
          <div><Label>Km actuels</Label><Input type="number" value={form.kilometrage} onChange={e => setForm({ ...form, kilometrage: e.target.value })} /></div>
          <div>
            <Label>Carburant</Label>
            <Select value={form.carburant} onValueChange={v => setForm({ ...form, carburant: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="diesel">Diesel</SelectItem>
                <SelectItem value="essence">Essence</SelectItem>
                <SelectItem value="electrique">Électrique</SelectItem>
                <SelectItem value="hybride">Hybride</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div><Label>Conso (L/100km)</Label><Input type="number" step="0.1" value={form.consommation} onChange={e => setForm({ ...form, consommation: e.target.value })} /></div>
        </div>

        <p className="text-xs text-slate-400 mt-2 border-t pt-3">Prochaines échéances (optionnel — pour les alertes)</p>
        <div className="grid grid-cols-2 gap-3">
          <div><Label>Contrôle technique</Label><Input type="date" value={form.prochain_controle_technique} onChange={e => setForm({ ...form, prochain_controle_technique: e.target.value })} /></div>
          <div><Label>Assurance</Label><Input type="date" value={form.prochaine_assurance} onChange={e => setForm({ ...form, prochaine_assurance: e.target.value })} /></div>
          <div><Label>Vidange (date)</Label><Input type="date" value={form.prochaine_vidange_date} onChange={e => setForm({ ...form, prochaine_vidange_date: e.target.value })} /></div>
          <div><Label>Vidange (km)</Label><Input type="number" value={form.prochaine_vidange_km} onChange={e => setForm({ ...form, prochaine_vidange_km: e.target.value })} placeholder="160000" /></div>
          <div><Label>Taxe circulation</Label><Input type="date" value={form.prochaine_taxe} onChange={e => setForm({ ...form, prochaine_taxe: e.target.value })} /></div>
          <div><Label>Changement pneus</Label><Input type="date" value={form.prochain_changement_pneus} onChange={e => setForm({ ...form, prochain_changement_pneus: e.target.value })} /></div>
        </div>

        <div>
          <Label>Notes (libres)</Label>
          <Textarea rows={3} value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} placeholder="Remarques personnelles sur ce véhicule..." data-testid="vehicule-notes" />
        </div>

        <Button type="submit" className="w-full bg-indigo-600 hover:bg-indigo-700" data-testid="submit-vehicule">{editing ? "Enregistrer" : "Créer"}</Button>
      </form>
    </DialogContent>
  </Dialog>
);

export default Vehicules;
